// /consent/EducationalMaterialModal.js
import React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Typography,
} from "@mui/material";

export default function EducationalMaterialModal({ open, onClose, facilityName }) {
    const displayFacilityName = facilityName || "facility_name";

    const dailyRiskReportUrl = "/educational material/daily risk report.pdf";
    const sampleCcmReportUrl = "/educational material/sample ccm report.pdf";
    const monitoringImageUrl = "/educational material/image.png";
    const monitoringVideoUrl = "/educational material/video of monitoring.mp4";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Educational Material</DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
                    <Box
                        component="img"
                        src="/2023 Tapestry Logo-400x80.png"
                        alt="TapestryHealth Logo"
                        sx={{
                            maxWidth: "100%",
                            width: 400,
                            height: "auto",
                            objectFit: "contain",
                        }}
                    />
                </Box>

                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
                    A partner of {displayFacilityName}
                </Typography>

                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    {displayFacilityName} has partnered with TapestryHealth to assist their team in providing the highest
                    quality and most timely care to its residents. TapestryHealth uses the latest technology and skilled
                    healthcare providers to proactively identify when a resident’s health starts to decline. We help to create
                    a plan of care to avoid hospitalizations and emergency room visits or to get residents to the hospital
                    faster, when necessary.
                    <br />
                    <br />
                    With TapestryHealth, your care team knows how each patient is doing every day. They know who needs more
                    care or supervision and who may need adjustments to their care plan. Our goal is to provide the care that
                    each resident needs by identifying and treating problems earlier rather than having to go the hospital
                    after a patient’s condition has worsened.
                    <br />
                    <br />
                    TapestryHealth provides facilities with a safety net of solutions that integrate seamlessly with existing
                    workflows to create a personalized, proactive approach for each patient. These solutions may include Daily
                    Risk Reports, Chronic Care Management Reports, and Remote Physiologic Monitoring. These services are
                    approved by the Centers for Medicare and Medicaid (CMS) to meet the complex health care needs of patients
                    with multiple chronic conditions.
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: 800 }}>
                    Chronic Care Management (CCM)
                </Typography>

                <Typography variant="body2" sx={{ lineHeight: 1.7, mt: 1 }}>
                    Chronic Care Management provides facilities with a Daily Risk Report that highlights the residents that are
                    most at-risk of experiencing an acute event. It also includes a monthly Chronic Care Management report that
                    assesses medications, changes in vital signs, changes to daily activities, and other health status
                    indicators. Each patient’s monthly report also has a detailed care plan that is updated and followed up on
                    as health status changes.
                </Typography>

                <Box sx={{ mt: 2 }}>
                    <Link
                        href={dailyRiskReportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontWeight: 800 }}
                    >
                        SAMPLE DAILY RISK REPORT
                    </Link>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Link
                        href={sampleCcmReportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontWeight: 800 }}
                    >
                        SAMPLE CCM REPORT
                    </Link>
                </Box>

                <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: 800 }}>
                    Remote Physiologic Monitoring (RPM)
                </Typography>

                <Typography variant="body2" sx={{ lineHeight: 1.7, mt: 1 }}>
                    Remote Physiologic Monitoring collects and reports vital signs, such as pulse and respiration rate, that
                    are early indicators of declining health status – in real time. Changes in vital signs that indicate a
                    decline in health status create notifications to prompt action by the on-site care team. Over time, Remote
                    Physiologic Monitoring picks up small – but important - changes in vital signs which often go unnoticed
                    during routine rounds and doctor visits. These changes can indicate that a resident is starting to develop
                    signs of a condition that could worsen. We help {displayFacilityName} treat patients faster and onsite,
                    when possible, so that ED visits and hospitalizations can be avoided – or if hospitalization is needed, the
                    problem is identified earlier.
                </Typography>

                <Box sx={{ mt: 2 }}>
                    <Box
                        component="img"
                        src={monitoringImageUrl}
                        alt="Remote Physiologic Monitoring"
                        sx={{
                            width: "100%",
                            maxHeight: 360,
                            objectFit: "contain",
                            borderRadius: 2,
                            border: "1px solid #e5e7eb",
                            bgcolor: "#f9fafb",
                        }}
                    />
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Link
                        href={monitoringVideoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontWeight: 800 }}
                    >
                        VIDEO OF MONITORING
                    </Link>
                </Box>

                <Typography variant="body2" sx={{ mt: 3, lineHeight: 1.7 }}>
                    TapestryHealth’s technology does not replace physicians, nurses, or anyone else at your skilled nursing
                    center. It helps the busy caregivers by providing additional support through data-driven, proactive, care
                    management and coordination so {displayFacilityName} residents enjoy continued health and quality of life.
                    <br />
                    <br />
                    Want to learn more about us? Check out{" "}
                    <a href="https://www.tapestryhealth.com" target="_blank" rel="noopener noreferrer">
                        www.tapestryhealth.com
                    </a>
                    <br />
                    <br />
                    <strong>Disclaimer:</strong> Not every facility is contracted for all services listed. To learn more about
                    which services your facility is contracted for, reach out with your facility administrator.
                </Typography>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}